import { GetConsumptionResponse } from "./types";
import { useQuery } from "@tanstack/react-query";
import { getDaysPastDate, toIsoString } from "../utils/date";
import { dashboardBFF } from "../axios-instances";
import { QueryClientScope } from "../types";
import { getEnv } from "src/config/index";
import { useFeatureToggle } from "src/api/featureToggling/useGetFeatureToggle";
import { FeatureToggleKey } from "src/api";
import { getServices } from "src/logs";

const { logger } = getServices();

// tip for testing local (e.g. Mockoon stubbed BE) you can set REACT_APP_BACKEND_URL=http://localhost:5000 in .env.development
const refreshInterval =
  getEnv().REACT_APP_BE_REFRESH_INTERVAL ||
  (() => {
    logger.logError({ message: "REACT_APP_BE_REFRESH_INTERVAL environmental variable not set" });
    throw new Error("REACT_APP_BE_REFRESH_INTERVAL environmental variable not set");
  })();

export const getConsumption =
  (logsDetailedFeature?: boolean | undefined, numberOfDays?: number) => async () => {
    try {
      const no_of_days = numberOfDays || 1;
      const date = getDaysPastDate(no_of_days);
      const dateFrom = toIsoString(date.startOf("day"));
      const dateTo = toIsoString(date.endOf("day"));

      // if ((process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "development") {
      //   console.log(
      //     "getConsumption:",
      //     `/api/water-consumption?api-version=1.0&datefrom=${encodeURIComponent(dateFrom)}&dateto=${encodeURIComponent(
      //       dateTo
      //     )}`
      //   );
      // }

      const response = await dashboardBFF.get(
        `/api/water-consumption?api-version=1.0&datefrom=${encodeURIComponent(
          dateFrom
        )}&dateto=${encodeURIComponent(dateTo)}`,
        { timeout: 3000 }
      );

      if (response.status === 204) {
        // no content
        const err = new Error("No content, http status code: 204");
        throw err;
      }

      if (logsDetailedFeature) {
        logger.logTrace({
          message: "getComsumption - response",
          properties: {
            confidencePercentage: response.data.confidencePercentage.toString(),
            consumption: response.data.consumption.toString(),
            consumptionYesterday: response.data.consumptionYesterday.toString(),
            lastUpdated: response.data.lastUpdated?.toString() ?? "N/A",
            trend: response.data.trend.toString(),
            trendValue: response.data.trendValue.toString()
          }
        });
      }
      return response.data as GetConsumptionResponse;
    } catch (error) {
      const err = error as Error;
      if (!err.message.includes("204")) {
        logger.logError({
          message: "getComsumption - Exception",
          properties: {
            name: err.name,
            message: err.message,
            stack: err.stack || "unknown stack",
            cause: String(err.cause ?? "unknown cause")
          }
        });
      }
      throw error;
    }
  };

export const useGetConsumptionData = (numberOfDays?: number) => {
  const { data: logsDetailedFeature } = useFeatureToggle(FeatureToggleKey.LogsDetailed);
  return useQuery<GetConsumptionResponse, Error>({
    queryKey: [QueryClientScope.Consumption, numberOfDays],
    queryFn: getConsumption(logsDetailedFeature?.isEnabled, numberOfDays),
    refetchOnWindowFocus: false,
    retry: 3, // Retry 3 times
    retryDelay: 3000, // Wait 3 seconds between each retry (in milliseconds)
    refetchInterval: parseInt(refreshInterval)
  });
};

